import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { IconButton } from "ui/icon-button";

import type { CampaignAttributes } from "hooks/api/use-campaigns";

import { Route as AppRoute } from "utilities/app-routes";
import { formatDateTime } from "utilities/date";

import { BasicTableActionsProps } from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { NamedDictionaryItem } from "hooks/api/dictionary/dictionary.types";

import Icons from "assets";

interface TableActionsProps extends BasicTableActionsProps {
  programTypes: NamedDictionaryItem[];
}

const headerStyles = "text-gray-600 font-bold leading-5";

export const createCampaignsTableColumns = ({
  pathname,
  programTypes,
}: TableActionsProps): ColumnDef<CampaignAttributes>[] => [
  {
    id: "name",
    header: "Campaign name",
    accessorKey: "name",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center gap-3 pr-3">
        <Link
          to={`${pathname}/${row.original.id}/${AppRoute.creative}`}
          className="text-aqua-600 hover:text-aqua-700 font-semibold text-base"
          aria-disabled={false}
          tabIndex={undefined}>
          {row.original.name}
        </Link>
      </div>
    ),
    size: 500,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "programType",
    header: "Program type",
    accessorKey: "programType",
    enableSorting: true,
    cell: ({ row }) => {
      const programType = programTypes.find((type) => type.name === row.original.programType)!;

      return <div className="text-gray-600">{programType?.description}</div>;
    },
    size: 225,
    meta: {
      headerClassName: `${headerStyles} pl-3`,
    },
  },
  {
    id: "lastModifiedDate",
    header: "Last updated",
    accessorKey: "lastModifiedDate",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
    ),
    size: 210,
    meta: {
      headerClassName: `${headerStyles} pl-3`,
    },
  },
  {
    id: "actions",
    header: "Actions",
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center justify-center gap-5">
        <IconButton
          variant="text"
          to={`${pathname}/${row.original.id}/${AppRoute.creative}`}
          aria-label="View campaign"
          data-testid="view-campaign-button"
          size="medium">
          <Icons.EyeVisible />
        </IconButton>
      </div>
    ),
    size: 81,
    meta: {
      className: "pr-8",
      isHeaderCentered: true,
      isPinnedRight: true,
    },
  },
];
